import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Link as LinkMUI,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Avatar from '@mui/material/Avatar';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PropTypes from 'prop-types';
import { getAccount, getAppUsers, getToken } from '../../../shared/api/api';
import useAuth from '../../../shared/lib/hooks/useAuth';
import VkAuth from './auth';
// eslint-disable-next-line import/no-unresolved
import { AUTH_FIELDS, authActions, clearStorage } from '../../../entities/auth/tokenResource/redux/tokenResource';
import { useAppDispatch } from '../../../app/store';
import DriverAppIcon from '../../../shared/ui/DriverAppIcon/DriverAppIcon';
import SchoolBusAppIcon from '../../../shared/ui/SchoolBusAppIcon/SchoolBusAppIcon';
import RuStoreBanner from '../../../assets/icons/RuStoreBanner';
import LoginForm from '../../../widgets/auth/LoginForm';
import MaintenanceBanner from '../../../widgets/auth/MaintenanceBanner';
import parseMaintenanceEnv from '../../../shared/lib/utils/parseMaintenanceEnv';
import config from '../../../shared/api/config';

// const DRIVER_APP_HREF = 'https://vm.vistar.ru/distr/vistarm_driver.apk';
// const SCHOOL_BUS_APP_HREF = 'https://vm.vistar.ru/distr/school_bus.apk';

const DRIVER_APP_HREF = 'https://www.rustore.ru/catalog/app/su.vistar.vistarm';
const SCHOOL_BUS_APP_HREF = 'https://www.rustore.ru/catalog/app/su.vistar.vistarm.passenger';

function LoadingAuth() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: '30px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '15px',
          textAlign: 'center',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" component="div" sx={{ flexGrow: 1, width: '100%' }}>
          ВИСТАР-М
        </Typography>
        <CircularProgress />
      </Box>
    </Box>
  );
}

function LoginView({ impersonize }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { signin } = useAuth();
  const dispatch = useAppDispatch();

  const pathname = location.state?.from?.pathname || '/';
  const searchParams = location.state?.from?.search || '';
  const params = new URLSearchParams(searchParams);
  // если есть state - переход на авторизацию был из-за проблемы с токеном
  const from = params.has('state') ? params.get('state') : `${pathname}${searchParams}`;

  const [token, updateToken] = useState(null);
  const [error, setError] = useState(null);
  const [load, setLoad] = useState(true);

  // Maintenance mode state
  const maintenanceInterval = useMemo(() => parseMaintenanceEnv(config.REACT_APP_MAINTENANCE), []);
  const maintenance = useMemo(() => {
    if (!maintenanceInterval) return null;
    const now = new Date();
    const { start, until } = maintenanceInterval;
    const sevenDaysBefore = new Date(start.getTime() - 7 * 24 * 60 * 60 * 1000);
    const isUpcoming = now >= sevenDaysBefore && now < start;
    const isActive = now >= start && now <= until;
    if (!isUpcoming && !isActive) return null;
    return { ...maintenanceInterval, isActive, isUpcoming };
  }, [maintenanceInterval]);

  // Keyboard bypass: Ctrl+Y Ctrl+M shows form during maintenance
  const [showFormBypass, setShowFormBypass] = useState(false);

  useEffect(() => {
    let ctrlYBuffer = [];
    const handler = (e) => {
      if (e.ctrlKey && e.code === 'KeyY') {
        ctrlYBuffer.push('Y');
        setTimeout(() => { ctrlYBuffer = []; }, 1000);
      }
      if (ctrlYBuffer.length > 0 && e.ctrlKey && e.code === 'KeyM') {
        setShowFormBypass(true);
        ctrlYBuffer = [];
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleLoginFormSubmit = (credentials, newToken = null) => {
    const { username: user, password: pass, rememberMe: remember } = credentials;
    let requestToken;
    if (!newToken) {
      requestToken = getToken({ username: user, password: pass, rememberMe: remember });
    } else {
      requestToken = Promise.resolve(newToken);
    }
    requestToken
      .then((res) => {
        clearStorage();
        // eslint-disable-next-line no-undef
        if (remember) window.localStorage.setItem('token', res);
        // eslint-disable-next-line no-undef
        else window.sessionStorage.setItem('token', res);
        dispatch(authActions.login({ [AUTH_FIELDS.TOKEN_ACCESS]: res }));
        updateToken(res);
      })
      // eslint-disable-next-line no-shadow
      .catch((error) => {
        if (error.status === 401) {
          setError('Неправильный логин или пароль');
        } else {
          setError('Произошла ошибка');
        }
        throw error;
      });
  };

  useEffect(() => {
    if (impersonize) {
      const newToken = window.sessionStorage.getItem('token');
      if (token) {
        handleLoginFormSubmit({}, newToken);
      }
    }
  }, [impersonize]);

  useEffect(() => {
    const accessToken = window.localStorage.getItem('token') || window.sessionStorage.getItem('token');

    if (!accessToken) {
      setLoad(false); // токена нет — показываем форму
      return;
    }

    updateToken(accessToken);

    getAccount()
      .then((userCurrent) => {
        let redirectUrl = from;
        if (!userCurrent.activated && impersonize) {
          // При попытке имперсонизации неактивного пользователя, возвращаемся к странице админа
          const atoken = window.sessionStorage.getItem('atoken');
          clearStorage();
          window.sessionStorage.setItem('token', atoken);
          toast.error('Пользователь неактивирован');
          navigate(redirectUrl || '/', { replace: true });
          return;
        }
        if (userCurrent.login) {
          getAppUsers({ userId: userCurrent.id })
            .then((appUser) => {
              const currentUser = appUser[0];
              const userInfo = {
                appUserId: currentUser.appUserId,
                company: currentUser.company,
                ...userCurrent,
              };
              if (
                impersonize &&
                !currentUser.role?.includes('ROLE_COMPANY_ADMIN') &&
                !currentUser.role?.includes('ROLE_ADMIN')
              ) {
                // У рядового пользователя может не оказаться доступа к текущему роуту,
                // возвращаем его в начало:
                redirectUrl = '/';
              }
              if (redirectUrl === '/') {
                const visitVistarM = window.localStorage.getItem('visitVistarM');
                if (visitVistarM) {
                  redirectUrl = '/companies';
                  window.localStorage.setItem('visitVistarM', true);
                } else {
                  redirectUrl = '/quick-start';
                  window.localStorage.setItem('visitVistarM', true);
                }
              }
              signin(userInfo, () => {
                navigate(redirectUrl, { replace: true });
              });
            })
            .finally(() => setLoad(false));
        }
        if (!token) setError('Неправильный логин или пароль');
      })
      .catch(() => {
        signin(null, () => {});
        setLoad(false);
      });
  }, [token, signin, from, navigate]);

  return !load && !impersonize ? (
    <Container component="main" maxWidth="xs">
      <>
        {maintenance && (
          <Box sx={{ marginTop: 6 }}>
            <MaintenanceBanner
              start={maintenance.start}
              until={maintenance.until}
              isActive={maintenance.isActive}
            />
          </Box>
        )}
        {(!maintenance?.isActive || showFormBypass) ? (
          <Box
            sx={{
              marginTop: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: 1,
            }}
          >
            <Avatar sx={{ m: 1 }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              Авторизация
            </Typography>
            <LoginForm onSubmit={handleLoginFormSubmit} error={error} isLoading={false} showForgotPassword />
          </Box>
        ) : null}
        {(!maintenance?.isActive || showFormBypass) ? <VkAuth /> : null}
        <Typography
          sx={{
            mt: 3,
            mb: 1,
            textAlign: 'center',
            justifyContent: 'space-around',
          }}
        >
          Установить приложение для Android:
        </Typography>
        <List sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
          <ListItem
            component={LinkMUI}
            href={DRIVER_APP_HREF}
            underline="hover"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              width: '50%',
              minWidth: '164px',
            }}
          >
            <DriverAppIcon sx={{ width: '36px', height: '36px', borderRadius: '4px' }} />
            <ListItemText
              primary={
                <Typography sx={{ lineHeight: '1rem', my: 1 }}>
                  ВИСТАР-М. <br />
                  Водитель
                </Typography>
              }
            />
            <RuStoreBanner />
          </ListItem>
          <ListItem
            component={LinkMUI}
            href={SCHOOL_BUS_APP_HREF}
            underline="hover"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              width: '50%',
              minWidth: '164px',
            }}
          >
            <SchoolBusAppIcon sx={{ width: '36px', height: '36px', borderRadius: '4px' }} />
            <ListItemText
              primary={
                <Typography sx={{ lineHeight: '1rem', my: 1 }}>
                  ВИСТАР-М. <br />
                  Школьный автобус
                </Typography>
              }
            />
            <RuStoreBanner />
          </ListItem>
        </List>
      </>
    </Container>
  ) : (
    <LoadingAuth />
  );
}

LoginView.propTypes = {
  impersonize: PropTypes.bool,
};

LoginView.defaultProps = {
  impersonize: false,
};

export default LoginView;
